import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import { Link } from '@reach/router';
import PopupWrapper from '../PopupWrapper';
import Button from '../Button/Button';
import { AnimatePresence, motion } from 'framer-motion';
import { INPUT_TYPES } from '../Input/Input';
import ChannelingToggleChangesetRow from './ChannelingToggleChangesetRow';
import { withGlobalContext } from '../../App';
import Input from '../Input/Input';
import Main from '../Main/Main';
import Loader from '../Loader/Loader';
import axiosUI from '../../axiosUI';
import { NOTIFICATIONS } from '../../constants';

const getAllRowsSelected = (selected) => {
  if (Object.keys(selected).length === 0) return true;

  for (let key in selected) {
    if (!selected[key]) {
      return false;
    }
  }

  return true;
};

const ToggleChannelingChangeset = ({ createNotification }) => {
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState({});
  const [state, setState] = useState('idle');
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const allRowsSelected = getAllRowsSelected(selected);

  useEffect(() => {
    try {
      setState('pending');
      fetchToggleDrafts();
    } catch (error) {
      setState('error');
    }
  }, []);

  useEffect(() => {
    const selectedChannels = drafts.reduce((map, draft) => {
      map[getUniqueKey(draft)] = true;
      return map;
    }, {});

    setSelected(selectedChannels);
  }, [drafts]);

  const fetchToggleDrafts = async () => {
    const { data } = await axiosUI.get('/api/channels/toggle', {
      params: { state: 'DRAFT' },
    });

    setDrafts(data);
    setState('resolved');
  };

  const getUniqueKey = (draft) => {
    return `${draft.channel_type}_${draft.operation_type}`;
  };

  const getSelectedItems = () => {
    return drafts.filter((draft) => selected[getUniqueKey(draft)]);
  };

  const toggleRowSelection = (event, draft) => {
    setSelected({
      ...selected,
      [getUniqueKey(draft)]: event.target.checked,
    });
  };

  const toggleSelectAllRows = (initialValue) => {
    const selections = drafts.reduce((map, draft) => {
      map[getUniqueKey(draft)] = !initialValue;
      return map;
    }, {});
    setSelected(selections);
  };

  const discardChanges = async () => {
    const itemsToDelete = getSelectedItems();
    setState('pending');

    try {
      await axiosUI.delete('/api/channels/toggle', {
        data: itemsToDelete.map(({ channel_type, status, state }) => ({
          channel_type,
          status,
          operation_type: 'DELETE',
          state,
        })),
      });

      toggleDiscardWarningPopup();
      createNotification({
        message: `Changes are discarded`,
        type: NOTIFICATIONS.TYPE.INFO,
        timeout: 5000,
      });

      await fetchToggleDrafts();

      setState('resolved');
    } catch (err) {
      toggleDiscardWarningPopup();
      setState('error');
      createNotification({
        message: 'Failed to discard changes',
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 3000,
      });
    }
  };

  const approveChanges = () => {
    setState('pending');
    const itemsToApprove = getSelectedItems();

    const approveChannels = async () => {
      try {
        await axiosUI.post(
          '/api/channels/toggle/approve',
          itemsToApprove.map(({ channel_type }) => ({
            channel_type,
          }))
        );
        setState('resolved');

        fetchToggleDrafts();

        createNotification({
          message: `Changes are approved`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });
      } catch (error) {
        console.log(error);

        setState('error');

        createNotification({
          message: `Failed to approve changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }
    };

    approveChannels();
  };

  const publishChanges = () => {
    setState('pending');
    const itemsToPublish = getSelectedItems();

    const publishChanges = async () => {
      try {
        await axiosUI.post(
          '/api/channels/toggle/publish',
          itemsToPublish.map(({ channel_type }) => ({
            channel_type,
          }))
        );

        setState('resolved');

        fetchToggleDrafts();

        createNotification({
          message: `Changes are published`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });
      } catch (error) {
        console.log(error);

        setState('error');

        createNotification({
          message: 'Failed to publish changes',
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }
    };

    publishChanges();
  };

  const toggleDiscardWarningPopup = () =>
    setIsDiscardDialogOpen(!isDiscardDialogOpen);
  const selectedItems = getSelectedItems();
  const currentSelectedItems = selectedItems.length > 0;
  const canPublishSelectedChanges =
    currentSelectedItems &&
    selectedItems.every(
      (draft) => draft.first_approver && draft.second_approver
    );

  return (
    <Fragment>
      <Sidebar highlightNavItemIndex={3} />
      <Topbar className="banners_changeset__topbar">
        <span className="font-bold text-lg capitalize">
          <Link className="bb-1-blue no-underline text-blue" to="/channeling">
            Channeling
          </Link>{' '}
          / Review Changes
        </span>
        <span className="float-right text-right">
          <PopupWrapper
            className="relative"
            isOpen={isDiscardDialogOpen}
            togglePopup={toggleDiscardWarningPopup}>
            <Button
              type="red-transparent"
              className="h-8"
              onClick={toggleDiscardWarningPopup}>
              Discard Changes
            </Button>
            <AnimatePresence>
              {isDiscardDialogOpen ? (
                <motion.div
                  initial={{ opacity: 0, y: '80%' }}
                  animate={{ opacity: 1, y: '100%' }}
                  exit={{ opacity: 0, y: '80%' }}
                  transition={{
                    ease: [0.18, 0.89, 0.39, 1.4],
                    duration: 0.3,
                  }}
                  className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                  <div className="text-center leading-normal">
                    Are you sure you want to discard selected changes?
                  </div>
                  <div className="flex justify-around mt-8">
                    <Button className="h-8" onClick={toggleDiscardWarningPopup}>
                      No
                    </Button>
                    <Button
                      type="red-transparent"
                      className="h-8"
                      onClick={discardChanges}>
                      Yes, discard them
                    </Button>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </PopupWrapper>
          <Button
            type="blue"
            className="h-8 h-8 ml-8"
            disabled={!currentSelectedItems}
            onClick={approveChanges}>
            Approve Changes
          </Button>
          <Button
            type="green"
            className="h-8 h-8 ml-8"
            disabled={!canPublishSelectedChanges}
            onClick={publishChanges}>
            Publish Changes
          </Button>
        </span>
      </Topbar>
      <Main className="banners_changeset__main relative">
        <Loader loading={state === 'pending'}>
          <table className="text-left w-full border-collapse bg-white table-fixed">
            <thead>
              <tr>
                <th className="p-2 w-16">
                  <Input
                    id="select_all"
                    name="select_all"
                    type={INPUT_TYPES.CHECKBOX}
                    onChange={() => toggleSelectAllRows(allRowsSelected)}
                    checked={allRowsSelected}
                    inputClassName="box_checkbox"
                  />
                </th>
                <th className="p-2 font-normal w-32">Updated By</th>
                <th className="p-2 font-normal w-32">Updated On</th>
                <th className="p-2 font-normal w-24">Type</th>
                <th className="p-2 font-normal w-24">Status</th>
                <th className="p-2 font-normal w-32 text-right">Approvals</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft) => {
                let totalApprovals = 0;
                if (draft.first_approver && draft.first_approver.trim()) {
                  totalApprovals += 1;
                }
                if (draft.second_approver && draft.second_approver.trim()) {
                  totalApprovals += 1;
                }
                const key = getUniqueKey(draft);

                return (
                  <ChannelingToggleChangesetRow
                    key={key}
                    rowKey={key}
                    draft={draft}
                    totalApprovals={totalApprovals}
                    selected={selected}
                    toggleRowSelection={toggleRowSelection}
                  />
                );
              })}
            </tbody>
          </table>
        </Loader>
      </Main>
    </Fragment>
  );
};

ToggleChannelingChangeset.propTypes = {
  createNotification: PropTypes.func.isRequired,
};

export default  withGlobalContext(ToggleChannelingChangeset);
